import * as THREE from 'three';
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls.js'

export function initGalaxy(canvas) {
    const scene = new THREE.Scene()

    const textureLoader = new THREE.TextureLoader()
    const particleTexture = textureLoader.load('/textures/particles/5.png')

    textureLoader.load('/textures/environmentMap/space.jpg', (texture) => {
        texture.mapping = THREE.EquirectangularReflectionMapping
        texture.colorSpace = THREE.SRGBColorSpace
        scene.background = texture
    })

    const parameters = {
        count: 180000,
        size: 0.01,
        radius: 5,
        branches: 5,
        spin: 1,
        randomness: 0.35,
        randomnessPower: 4,
        insideColor: '#ff5a00',
        outsideColor: '#1b3984'
    }

    let geometry = null
    let material = null
    let points = null

    const colorInside = new THREE.Color()
    const colorOutside = new THREE.Color()
    const mixedColor = new THREE.Color()

    const generateGalaxy = () => {
        if (points !== null) {
            geometry.dispose()
            material.dispose()
            scene.remove(points)
        }

        geometry = new THREE.BufferGeometry()
        const positions = new Float32Array(parameters.count * 3)
        const colors = new Float32Array(parameters.count * 3)

        colorInside.set(parameters.insideColor)
        colorOutside.set(parameters.outsideColor)

        for (let i = 0; i < parameters.count; i++) {
            const i3 = i * 3
            const radius = Math.random() * parameters.radius
            const spinAngle = radius * parameters.spin
            const branchAngle = (i % parameters.branches) / parameters.branches * Math.PI * 2
            const randomX = Math.pow(Math.random(), parameters.randomnessPower) * (Math.random() < 0.5 ? 1 : -1) * parameters.randomness * radius
            const randomY = Math.pow(Math.random(), parameters.randomnessPower) * (Math.random() < 0.5 ? 1 : -1) * parameters.randomness * radius
            const randomZ = Math.pow(Math.random(), parameters.randomnessPower) * (Math.random() < 0.5 ? 1 : -1) * parameters.randomness * radius

            positions[i3]     = Math.cos(branchAngle + spinAngle) * radius + randomX
            positions[i3 + 1] = randomY
            positions[i3 + 2] = Math.sin(branchAngle + spinAngle) * radius + randomZ

            mixedColor.copy(colorInside).lerp(colorOutside, radius / parameters.radius)
            colors[i3]     = Math.pow(mixedColor.r, 2.2)
            colors[i3 + 1] = mixedColor.g
            colors[i3 + 2] = mixedColor.b
        }

        geometry.setAttribute('position', new THREE.BufferAttribute(positions, 3))
        geometry.setAttribute('color', new THREE.BufferAttribute(colors, 3))

        material = new THREE.PointsMaterial({
            size: parameters.size,
            sizeAttenuation: true,
            depthWrite: false,
            blending: THREE.AdditiveBlending,
            vertexColors: true,
            alphaMap: particleTexture,
            transparent: true
        })

        points = new THREE.Points(geometry, material)
        scene.add(points)
    }

    generateGalaxy()

    let starsGeometry = null
    let starsMaterial = null

    const generateStars = () => {
        starsGeometry = new THREE.BufferGeometry()
        const starsCount = 1000
        const positions = new Float32Array(starsCount * 3)
        const colors = new Float32Array(starsCount * 3)
        const starColors = [
            [0.7, 0.8, 1.0], [1.0, 1.0, 1.0], [1.0, 1.0, 0.6],
            [1.0, 0.6, 0.2], [1.0, 0.3, 0.1]
        ]
        for (let i = 0; i < starsCount; i++) {
            const i3 = i * 3
            positions[i3]     = (Math.random() - 0.5) * 100
            positions[i3 + 1] = (Math.random() - 0.5) * 100
            positions[i3 + 2] = (Math.random() - 0.5) * 100
            const c = starColors[Math.floor(Math.random() * starColors.length)]
            colors[i3] = c[0]; colors[i3 + 1] = c[1]; colors[i3 + 2] = c[2]
        }
        starsGeometry.setAttribute('position', new THREE.BufferAttribute(positions, 3))
        starsGeometry.setAttribute('color', new THREE.BufferAttribute(colors, 3))
        starsMaterial = new THREE.PointsMaterial({
            size: 0.15, sizeAttenuation: true, depthWrite: false,
            blending: THREE.AdditiveBlending, vertexColors: true,
            transparent: true, alphaMap: particleTexture
        })
        scene.add(new THREE.Points(starsGeometry, starsMaterial))
    }

    generateStars()

    const sizes = { width: canvas.clientWidth, height: canvas.clientHeight }

    const camera = new THREE.PerspectiveCamera(75, sizes.width / sizes.height, 0.1, 100)
    camera.position.set(3, 3, 3)
    scene.add(camera)

    const controls = new OrbitControls(camera, canvas)
    controls.enableDamping = true
    controls.minDistance = 0
    controls.maxDistance = 7
    controls.maxPolarAngle = Math.PI / 1.5

    const renderer = new THREE.WebGLRenderer({ canvas, antialias: false })
    renderer.setSize(sizes.width, sizes.height, false)
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2))

    const resizeObserver = new ResizeObserver(() => {
        sizes.width = canvas.clientWidth
        sizes.height = canvas.clientHeight
        camera.aspect = sizes.width / sizes.height
        camera.updateProjectionMatrix()
        renderer.setSize(sizes.width, sizes.height, false)
    })
    resizeObserver.observe(canvas)

    let rafId = null
    const tick = () => {
        controls.update()
        renderer.render(scene, camera)
        rafId = window.requestAnimationFrame(tick)
    }
    tick()

    return {
        updateParams: (newParams) => {
            Object.assign(parameters, newParams)
            generateGalaxy()
        },
        cleanup: () => {
            window.cancelAnimationFrame(rafId)
            resizeObserver.disconnect()
            geometry?.dispose()
            material?.dispose()
            starsGeometry?.dispose()
            starsMaterial?.dispose()
            particleTexture.dispose()
            renderer.dispose()
        }
    }
}
